
import { Injectable } from '@angular/core';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

export interface DiagnosticIssue {
    severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    category: string;
    message: string;
    action: string;
}

@Injectable({ providedIn: 'root' })
export class DiagnosticsService {
  private supabase: SupabaseClient | null = getSupabase();

  // --- HEALTH SCANS ---

  async runDataIntegrityScan(): Promise<DiagnosticIssue[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('admin_scan_data_integrity');
    if (error) throw error;
    return data as DiagnosticIssue[];
  }

  async runSecurityAudit(): Promise<DiagnosticIssue[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase.rpc('admin_audit_security');
    if (error) throw error;
    return data as DiagnosticIssue[];
  }

  async getRecentAlerts(): Promise<any[]> {
    if (!this.supabase) return [];
    const { data, error } = await this.supabase
        .from('system_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
    if (error) throw error;
    return data;
  }

  // --- LOGIC VERIFICATION SUITE ---

  async verifyBillingLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_billing_logic');
    if (error) throw error;
    return data;
  }
  
  async verifyPaymentLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_payment_logic');
    if (error) throw error;
    return data;
  }

  async simulatePayment(storeId: string, amount: number, refId: string): Promise<any> {
      if (!this.supabase) return { status: 'MOCK', message: 'Simulator disabled in mock mode' };
      const { data, error } = await this.supabase.rpc('admin_simulate_payment_webhook', {
          p_target_store_id: storeId,
          p_amount: amount,
          p_ref_id: refId,
          p_type: 'TOPUP'
      });
      if (error) throw error;
      return data;
  }

  async verifyPartnerLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_partner_logic');
    if (error) throw error;
    return data;
  }

  async verifyInventoryLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_inventory_logic');
    if (error) throw error;
    return data;
  }
  
  async verifyMedicalLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_medical_logic');
    if (error) throw error;
    return data;
  }
  
  async verifyDispensingLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_dispensing_logic');
    if (error) throw error;
    return data;
  }
  
  async verifyFinancialSafety(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_financial_safety');
    if (error) throw error;
    return data;
  }

  async verifyReferralBilling(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_referral_billing');
    if (error) throw error;
    return data;
  }

  async verifyRegistrationLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_registration_logic');
    if (error) throw error;
    return data;
  }

  async verifyCustomerLogic(): Promise<any> {
    if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)', details: { note: 'DB not connected' } };
    const { data, error } = await this.supabase.rpc('admin_verify_customer_data_logic');
    if (error) throw error;
    return data;
  }
  
  // NEW: Auditor Verification
  async verifyAuditorPermissions(): Promise<any> {
      if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)' };
      const { data, error } = await this.supabase.rpc('admin_verify_auditor_permissions');
      if (error) throw error;
      return data;
  }

  // NEW: Clearing Logic Verification
  async verifyClearingLedger(): Promise<any> {
      if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)' };
      const { data, error } = await this.supabase.rpc('admin_verify_clearing_ledger');
      if (error) throw error;
      return data;
  }
  
  // NEW: Queue Logic Verification (Test Case 1 & 2)
  async verifyQueueLogic(): Promise<any> {
      if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)' };
      const { data, error } = await this.supabase.rpc('admin_verify_queue_logic');
      if (error) throw error;
      return data;
  }
  
  // NEW: Queue Load Verification (Test Case 3)
  async verifyQueueLoad(): Promise<any> {
      if (!this.supabase) return { status: 'MOCK', message: 'Passed (Mock Mode)' };
      const { data, error } = await this.supabase.rpc('admin_verify_queue_load');
      if (error) throw error;
      return data;
  }

  // --- INFRASTRUCTURE CHECKS ---

  async checkDatabaseHealth(): Promise<number> {
    if (!this.supabase) throw new Error("No Connection");
    const start = performance.now();
    const { error } = await this.supabase.from('products').select('id').limit(1);
    if (error) throw error;
    return performance.now() - start;
  }

  async checkEdgeFunctionHealth(): Promise<{ latency: number, status: 'ok' | 'not_found' | 'error', message?: string }> {
    if (!this.supabase) throw new Error("No Connection");
    const start = performance.now();
    const { error } = await this.supabase.functions.invoke('invoke_gemini', {
      body: { type: 'parse_order', payload: { prompt: 'test', productList: ['test'] } }
    });
    const latency = performance.now() - start;
    if (error) {
        const errString = JSON.stringify(error);
        if (errString.includes('404') || error.message?.includes('not found') || error.code === 'FUNCTION_NOT_FOUND') {
            return { latency, status: 'not_found', message: 'Function Not Deployed' };
        }
        return { latency, status: 'error', message: error.message || 'AI Engine Error' };
    }
    return { latency, status: 'ok' };
  }

  async checkRealtimeHealth(): Promise<{ connected: boolean, latency: number }> {
    if (!this.supabase) return { connected: false, latency: 0 };
    const channelId = `health-check-${Math.random().toString(36).substring(7)}`;
    const channel = this.supabase.channel(channelId);
    const start = performance.now();
    return new Promise((resolve) => {
        const timeout = setTimeout(() => { this.supabase?.removeChannel(channel); resolve({ connected: false, latency: 0 }); }, 3000);
        channel.on('broadcast', { event: 'ping' }, () => {
                const latency = performance.now() - start; clearTimeout(timeout); this.supabase?.removeChannel(channel); resolve({ connected: true, latency });
            })
            .subscribe(async (status) => { if (status === 'SUBSCRIBED') { await channel.send({ type: 'broadcast', event: 'ping', payload: {} }); } });
    });
  }

  async checkBrowserCapabilities(): Promise<{ camera: boolean, canvas: boolean }> {
      let camera = false;
      let canvas = false;
      if (navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') { camera = true; }
      const elem = document.createElement('canvas');
      if (!!(elem.getContext && elem.getContext('2d'))) { canvas = true; }
      return { camera, canvas };
  }
}
